import React, { useState, useRef } from 'react';
import { useToast } from '../context/ToastContext';
import '../styles/ImageUpload.css';

const ImageUpload = ({ 
  onImageSelect, 
  currentImage = null,
  maxSizeMB = 5,
  acceptedFormats = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'],
  label = 'Upload Image',
  aspectRatio = null,
  circular = false
}) => {
  const [preview, setPreview] = useState(currentImage);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const { error: showError, success: showSuccess } = useToast();

  const validateFile = (file) => {
    if (!acceptedFormats.includes(file.type)) {
      showError(`Please upload a valid image (${acceptedFormats.map(f => f.split('/')[1]).join(', ')})`);
      return false;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      showError(`Image size must be less than ${maxSizeMB}MB. Current size: ${sizeMB.toFixed(2)}MB`);
      return false;
    }

    return true;
  };

  const handleFileSelect = async (file) => {
    if (!file || !validateFile(file)) return;

    setIsLoading(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setIsLoading(false);
        showSuccess('Image selected successfully!');
      };
      reader.readAsDataURL(file);

      if (onImageSelect) {
        onImageSelect(file);
      }
    } catch (err) {
      console.error('Error processing image:', err);
      showError('Failed to process image. Please try again.');
      setIsLoading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageSelect) {
      onImageSelect(null);
    }
    showSuccess('Image removed');
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="image-upload-wrapper">
      <label className="image-upload-label">{label}</label>
      
      <div 
        className={`image-upload-container ${isDragging ? 'dragging' : ''} ${preview ? 'has-image' : ''} ${circular ? 'circular' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!preview ? triggerFileInput : undefined}
        style={aspectRatio ? { aspectRatio } : {}}
      >
        {isLoading ? (
          <div className="image-upload-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Processing image...</p>
          </div>
        ) : preview ? (
          <div className="image-upload-preview">
            <img 
              src={preview} 
              alt="Preview" 
              className={circular ? 'preview-circular' : ''}
            />
            <div className="image-upload-overlay">
              <button 
                type="button"
                className="btn btn-light btn-sm me-2"
                onClick={(e) => {
                  e.stopPropagation();
                  triggerFileInput();
                }}
              >
                Change
              </button>
              <button 
                type="button"
                className="btn btn-danger btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="image-upload-placeholder">
            <svg 
              width="48" 
              height="48" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              className="upload-icon"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p className="upload-text">
              <strong>Click to upload</strong> or drag and drop
            </p>
            <p className="upload-hint">
              {acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')} (Max {maxSizeMB}MB)
            </p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(',')}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

export default ImageUpload;